import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { useAuthContext } from './AuthContext';
import api from '../utils/api';

// Initial state
const initialState = {
  categories: [],
  loading: true,
  error: null
};

// Create context
const CategoryContext = createContext(initialState);

// Reducer function
const categoryReducer = (state, action) => {
  switch (action.type) {
    case 'GET_CATEGORIES':
      return {
        ...state,
        categories: action.payload,
        loading: false
      };
    case 'ADD_CATEGORY':
      return {
        ...state,
        categories: [...state.categories, action.payload]
      };
    case 'UPDATE_CATEGORY':
      return {
        ...state,
        categories: state.categories.map(category => 
          category._id === action.payload._id ? action.payload : category
        )
      };
    case 'DELETE_CATEGORY':
      return {
        ...state,
        categories: state.categories.filter(category => category._id !== action.payload)
      };
    case 'CATEGORY_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case 'CLEAR_CATEGORIES':
      return {
        ...state,
        categories: [],
        loading: false
      };
    default:
      return state;
  }
};

// Provider component
export const CategoryProvider = ({ children }) => {
  const [state, dispatch] = useReducer(categoryReducer, initialState);
  const { isAuthenticated } = useAuthContext();

  // Get all categories
  useEffect(() => {
    const fetchCategories = async () => {
      // Only fetch categories if user is authenticated
      if (!isAuthenticated) {
        dispatch({
          type: 'CLEAR_CATEGORIES'
        });
        return;
      }

      try {
        const res = await api.get('/categories');
        console.log('Fetched categories:', res.data);
        dispatch({
          type: 'GET_CATEGORIES',
          payload: res.data
        });
      } catch (err) {
        console.error('Error fetching categories:', err);
        dispatch({
          type: 'CATEGORY_ERROR',
          payload: err.response ? err.response.data.message : 'Error fetching categories'
        });
      }
    };

    fetchCategories();
  }, [isAuthenticated]);

  // Actions
  const addCategory = async (category) => {
    try {
      const res = await api.post('/categories', category);
      console.log('Added category:', res.data);
      dispatch({
        type: 'ADD_CATEGORY',
        payload: res.data
      });
      return res.data;
    } catch (err) {
      console.error('Error adding category:', err);
      dispatch({
        type: 'CATEGORY_ERROR',
        payload: err.response ? err.response.data.message : 'Error adding category'
      });
      throw err;
    }
  };

  const updateCategory = async (id, category) => {
    try {
      const res = await api.put(`/categories/${id}`, category);
      console.log('Updated category:', res.data);
      dispatch({
        type: 'UPDATE_CATEGORY',
        payload: res.data
      });
      return res.data;
    } catch (err) {
      console.error('Error updating category:', err);
      dispatch({
        type: 'CATEGORY_ERROR',
        payload: err.response ? err.response.data.message : 'Error updating category'
      });
      throw err;
    }
  };

  const deleteCategory = async (id) => {
    try {
      await api.delete(`/categories/${id}`);
      console.log('Deleted category:', id);
      dispatch({
        type: 'DELETE_CATEGORY',
        payload: id
      });
    } catch (err) {
      console.error('Error deleting category:', err);
      dispatch({
        type: 'CATEGORY_ERROR',
        payload: err.response ? err.response.data.message : 'Error deleting category'
      });
      throw err;
    }
  };

  return (
    <CategoryContext.Provider
      value={{
        categories: state.categories,
        loading: state.loading,
        error: state.error,
        addCategory,
        updateCategory,
        deleteCategory
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

// Custom hook to use the category context
export const useCategoryContext = () => useContext(CategoryContext);
